import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAppAlerts } from '@/stores/app/alerts';
import { useSideBySideView } from '@/stores/document/sideBySideView';
import { useAppRoute } from '@/stores/app/route';
import { v4 as uuidv4 } from 'uuid';
import { blockObject, setPreviousBlock } from '@/stores/editor/models/block';
import { sectionObject, setPreviousSection } from '@/stores/editor/models/section';
import { useDocumentEditor } from '@/stores/document/editor';
import { useHighlights } from '@/stores/highlights/highlights';
import { toNestedList } from '@/helpers/quillNestedList';
import { writeToClipboardExecCommand, writeToClipboardApi } from '@/helpers/copyToClipboard';
import { joinFooterAndHeaderFields } from '@/helpers/headerFooterFields';
import {
  TYPE_SUCCESSFACTORS, TYPE_TALEO, TYPE_TAPWAGE, TYPE_DPPLANYWHERE,
} from '@/helpers/constants/integrationTypes';
import { useAppBase } from '@/stores/app/base';
import { useAppSnackbar } from '@/stores/app/snackbar';
import {
  recruitJsonMakeHidden,
  recruitJsonUnmakeFlagship,
  recruitJsonDuplicateFlagship,
  recruitJsonMakePublic,
  recruitJsonMakePrivateUs,
} from '@/api/recruitJson';

export const useEditor = defineStore('editor', () => {
  const { addAlert, removeAlert } = useAppAlerts();
  const { addSnackbar } = useAppSnackbar();

  // State
  const fields = ref([]);
  const moveAnimation = ref({});
  const loadingAnimation = ref({ skeleton: true });
  const saveRequestStatus = ref('completed');
  const saveDebounceHandler = ref(null);

  // init request fields
  const atsEditUrl = ref('');
  const atsName = ref('');
  const atsIcon = ref('');
  const atsRequisitionId = ref(null);
  const atsTotalApplicants = ref(0);
  const companyName = ref('');
  const documentType = ref('');
  const hasIntegration = ref(false);
  const hasShareUsers = ref(false);
  const integrationTypeKey = ref(0);
  const isApplicationReportingEnabled = ref(false);
  const isPrivate = ref(false);
  const isExternal = ref(false);
  const isEvergreen = ref(false);
  const isLinked = ref(0);
  const isOpen = ref(false);
  const isPublished = ref(false);
  const isSharePage = ref(false);
  const isEditable = ref(false);
  const isHidden = ref(false);
  const jobStatusLabel = ref('');
  const jobId = ref('');
  const ownersActive = ref([]);
  const ownersLead = ref([]);
  const projectIntegrationType = ref(null);
  const projectIntegrationTypeKey = ref(null);
  const revisionId = ref(null);
  const shareNames = ref([]);
  const showInvite = ref(false);
  const showDuplicateModal = ref(false);
  const specificityKey = ref(0);
  const superuser = ref(false);
  const tapwageId = ref(null);
  const templateList = ref([]);
  const validUsers = ref([]);
  const wordCount = ref(0);
  const showWeeklyReports = ref(false);
  const isFlagship = ref(false);

  // document request (vueGet, rescore) fields
  const bodyMessages = ref({});
  const score = ref(0);
  const scales = ref({});
  const specificityData = ref({
    globalAny: 0, globalSimilar: 0, localAny: 0, localSimilar: 0, ratio: 0,
  });
  const flexibleId = ref('');
  const isSyncable = ref(false);
  const isPermissionTogglable = ref(false);
  const allRevisions = ref([]);
  const company = ref('');
  const location = ref('');
  const reqReportUrl = ref('');
  const title = ref('');
  const postSummary = ref({
    nonCompliantJobs: 0,
    employerBrandJobs: 0,
    jobPerformanceJobs: 0,
  });

  // Getters
  const isTemplate = computed(() => documentType.value === 'job_template');

  const documentTypeLabel = computed(() => ({
    job: 'Job',
    job_template: 'Job Template',
  })[documentType.value] || '');

  const blockTypes = computed(() => {
    const commonTypes = {
      paragraph: { label: 'Paragraph' },
      header: { label: 'Header' },
      list: { label: 'List' },
    };

    return commonTypes;
  });

  const blockList = computed(() => {
    const list = [];

    fields.value.forEach((field, fieldIndex) => {
      field.sections.forEach((section, sectionIndex) => {
        section.blocks.forEach((block, blockIndex) => {
          list.push(blockObject({
            block, blockIndex, section, sectionIndex, field, fieldIndex,
          }));
        });
      });
    });

    list.forEach((block, i) => setPreviousBlock(block, list[i - 1]));

    return list;
  });

  const blockMap = computed(() => blockList.value.reduce((res, block) => ({
    ...res,
    [block.id]: block,
  }), {}));

  const sectionList = computed(() => {
    const list = [];

    fields.value.forEach((field, fieldIndex) => {
      field.sections.forEach((section, sectionIndex) => {
        list.push(sectionObject({
          section, sectionIndex, field, fieldIndex,
        }));
      });
    });

    list.forEach((section, i) => setPreviousSection(section, list[i - 1]));

    return list;
  });

  const sectionMap = computed(() => sectionList.value.reduce((res, section) => ({
    ...res,
    [section.id]: section,
  }), {}));

  const fieldStructure = computed(() => {
    const result = {};

    fields.value.forEach(({ id: fieldId, sections }) => {
      result[fieldId] = {
        sectoins: sections.map((section) => ({
          id: section.id,
          formatBlacklist: section.formatBlacklist,
          lockType: section.lockType,
          type: section.type,
          blocks: section.blocks.map((b) => b.id),
        })),
      };
    });

    return result;
  });

  const revisionView = computed(() => useAppRoute().route?.query?.sideRevision);

  const subMenuProps = computed(() => ({
    allRevisions: allRevisions.value,
    atsEditUrl: atsEditUrl.value,
    atsName: atsName.value,
    atsIcon: atsIcon.value,
    company: company.value,
    companyName: companyName.value,
    hasIntegration: hasIntegration.value,
    hasShareUsers: hasShareUsers.value,
    isEvergreen: isEvergreen.value,
    isInternal: !isExternal.value && !isTemplate.value,
    isLinked: isLinked.value,
    isOpen: isOpen.value,
    isPrivate: isPrivate.value,
    isPublished: isPublished.value,
    isSharePage: isSharePage.value,
    isTemplate: isTemplate.value,
    isHidden: isHidden.value,
    jobId: jobId.value,
    jobStatusLabel: jobStatusLabel.value,
    jobTitle: title.value,
    location: location.value,
    ownersActive: ownersActive.value,
    ownersLead: ownersLead.value,
    projectIntegrationType: projectIntegrationType.value,
    projectIntegrationTypeKey: projectIntegrationTypeKey.value,
    revisionId: revisionId.value,
    shareNames: shareNames.value,
    showInvite: showInvite.value,
    superuser: superuser.value,
    isSyncable: isSyncable.value,
    isPermissionTogglable: isPermissionTogglable.value,
    tapwageId: tapwageId.value,
    validUsers: validUsers.value,
    isFlagship: isFlagship.value,
  }));

  const isRevisionSaving = computed(() => ['debouncing', 'saving'].includes(saveRequestStatus.value));

  const hasContent = computed(() => wordCount.value >= 10);

  const sideBySideView = computed(() => useSideBySideView().sideBySideView);

  const whichCompanyLocationLocked = computed(() => {
    let fieldLocked;

    if (!isEditable.value) {
      fieldLocked = 'all';
    } else if (isLinked.value && integrationTypeKey.value !== TYPE_DPPLANYWHERE) {
      fieldLocked = 'partial';
    } else {
      fieldLocked = 'none';
    }

    return fieldLocked;
  });

  const isPublishedTemplate = computed(
    () => isTemplate.value && !isHidden.value,
  );

  const isTapwageIntegration = computed(() => projectIntegrationTypeKey.value === TYPE_TAPWAGE);

  const showAtsMenu = computed(() => isLinked.value
    && hasIntegration.value
    && atsEditUrl.value
    && !isSharePage.value
    && ![TYPE_SUCCESSFACTORS, TYPE_TAPWAGE, TYPE_TALEO]
      .includes(projectIntegrationTypeKey.value));

  const showTapwageMenu = computed(() => isLinked.value
    && hasIntegration.value
    && isOpen.value
    && isPublished.value
    && !isSharePage.value
    && projectIntegrationTypeKey.value === TYPE_TAPWAGE);

  const document = computed(() => ({
    jobId: jobId.value,
    company: company.value,
    title: title.value,
    location: location.value,
    score: score.value,
    wordCount: wordCount.value,
    revisionId: revisionId.value,
    documentType: documentType.value,
    isFlagship: isFlagship.value,
    isTemplate: isTemplate.value,
    isLinked: isLinked.value,
    isOpen: isOpen.value,
    isPublished: isPublished.value,
    isSharePage: isSharePage.value,
    isEditable: isEditable.value,
  }));

  // Actions
  const setSelectionPromise = () => new Promise((resolve) => setTimeout(() => {
    resolve();
  }, 10));

  const getFieldHtml = (field) => {
    let html = '';

    field.sections.forEach((section) => {
      section.blocks.forEach((block) => {
        // we still might have really old jobs with quill indented lists.
        // convert them to proper nested lists
        const formattedList = toNestedList(block.html);
        html += formattedList;
      });
    });
    return html;
  };

  const findBlockById = (fieldsToUse, blockMapToUse, id) => {
    const { blockIndex, sectionIndex, fieldIndex } = blockMapToUse[id] || {};
    if (fieldIndex === undefined) {
      return {};
    }

    const field = fieldsToUse[fieldIndex] || {};
    if (!field.sections) {
      return {};
    }

    const section = field.sections[sectionIndex] || {};
    if (!sectionObject.blocks) {
      return {};
    }

    const block = section.blocks[blockIndex];
    return {
      field, fieldIndex, section, sectionIndex, block, blockIndex,
    };
  };

  const setDescriptionJson = ({ fields: fieldsParam = [] } = {}) => {
    fields.value = fieldsParam;
  };

  const updateFieldSections = ({ fieldIndex, sections }) => {
    fields.value[fieldIndex].sections = sections;
  };

  const setBlockInitialized = ({
    field, section, block, initialized,
  }) => {
    fields.value[field].sections[section].blocks[block].initialized = initialized;
  };

  const setBlockHtml = ({
    field, section, block, html,
  }) => {
    fields.value[field].sections[section].blocks[block].html = html;
  };

  const setBlockType = ({
    field, section, block, type,
  }) => {
    fields.value[field].sections[section].blocks[block].type = type;
  };

  const setBlockLockType = ({
    field, section, block, lockType,
  }) => {
    fields.value[field].sections[section].blocks[block].lockType = lockType;
  };

  const setSectionType = ({ field, section, type }) => {
    fields.value[field].sections[section].type = type;
  };

  const setSectionlockType = ({ field, section, lockType }) => {
    fields.value[field].sections[section].lockType = lockType;
  };

  // split section into 3 sections with a section containing only target block in the middle
  const splitSection = (section, blockIndex) => {
    const beforeBlocks = section.blocks.slice(0, blockIndex);
    const block = section.blocks[blockIndex];
    const afterBlocks = section.blocks.slice(blockIndex + 1);

    return [
      { ...section, blocks: beforeBlocks },
      {
        ...section, id: uuidv4(), blocks: [block],
      },
      { ...section, id: uuidv4(), blocks: afterBlocks },
    ];
  };

  const snapSameTypeSectionsTogether = (sections) => sections.filter((s) => s.blocks.length)
    .reduce((res, sect, i) => {
      const prevSection = res[res.length - 1];
      if (i === 0 || prevSection.type !== sect.type) {
        return [...res, sect];
      }

      // eslint-disable-next-line no-param-reassign
      res[res.length - 1] = { ...prevSection, blocks: [...prevSection.blocks, ...sect.blocks] };
      return res;
    }, []);

  const swapBlocks = ({ blockId, otherBlockId, blockMap: newBlockMap }) => {
    const fieldsToUse = JSON.parse(JSON.stringify(fields.value));

    const {
      field, fieldIndex, section, sectionIndex, block, blockIndex,
    } = findBlockById(fieldsToUse, newBlockMap, blockId);

    const {
      fieldIndex: fieldIndex2, section: section2, sectionIndex: sectionIndex2, block: block2,
      blockIndex: blockIndex2,
    } = findBlockById(fieldsToUse, newBlockMap, otherBlockId);

    if (!block || !block2) return;

    // do not swap blocks from different fields
    if (fieldIndex !== fieldIndex2) return;

    // blocks in the same section can be just swapped
    if (sectionIndex === sectionIndex2) {
      const tmp = section.blocks[blockIndex];
      section.blocks[blockIndex] = section.blocks[blockIndex2];
      section.blocks[blockIndex2] = tmp;

      fields.value = fieldsToUse;
      return;
    }

    // split the sections
    const newSections = splitSection(section, blockIndex);
    const newSections2 = splitSection(section2, blockIndex2);

    // swap single block sections
    const tmpSection = newSections[1];
    // eslint-disable-next-line prefer-destructuring
    newSections[1] = newSections2[1];
    newSections2[1] = tmpSection;

    // put updated sections to field.sections
    if (sectionIndex > sectionIndex2) {
      field.sections.splice(sectionIndex, 1, ...newSections);
      field.sections.splice(sectionIndex2, 1, ...newSections2);
    } else {
      field.sections.splice(sectionIndex2, 1, ...newSections2);
      field.sections.splice(sectionIndex, 1, ...newSections);
    }

    // snap same type sections together
    field.sections = snapSameTypeSectionsTogether(field.sections);
    // save updated fields
    fields.value = fieldsToUse;
  };

  const setDocumentType = (newDocumentType) => {
    documentType.value = newDocumentType;
  };

  const setMoveAnimation = ({ blockId, diff }) => {
    moveAnimation.value = { ...moveAnimation.value, [blockId]: diff };
  };

  const addToLoadingAnimation = (stageName) => {
    loadingAnimation.value = { ...loadingAnimation.value, [stageName]: true };
  };

  const setLoadingAnimation = (newLoadingAnimation) => {
    loadingAnimation.value = newLoadingAnimation;
  };

  const setFields = (data, newFields) => {
    Object.keys(newFields).forEach((field) => {
      if (data[field] !== undefined) {
        // eslint-disable-next-line no-param-reassign
        newFields[field].value = data[field];
      }
    });
  };

  const updateIsHidden = async (newValue, type = 'template') => {
    try {
      const { success } = await recruitJsonMakeHidden({ jobId: jobId.value, isHidden: newValue });
      if (success) {
        isHidden.value = newValue;

        if (newValue) {
          const text = {
            flagship: 'This job can now be edited',
          }[type] || 'This template is now hidden from the organization';

          addSnackbar({
            color: 'success',
            text,
            icon: '$checkbox-marked-circle',
          });
        } else {
          const text = {
            flagship: 'Your team can now find this job in the Flagship library',
          }[type] || 'This template is now available for the organization';

          addSnackbar({
            color: 'success',
            text,
            icon: '$checkbox-marked-circle',
          });
        }
      }

      if (isHidden.value) {
        removeAlert('publishedTemplate');
      } else if (type === 'template') {
        addAlert({
          id: 'publishedTemplate',
          type: 'info',
          message: 'This template is published and cannot be edited. To make changes, toggle "Template" to "Hidden," make updates and toggle it back.',
          thisPageOnly: true,
        });
      }
    } catch (e) {
      addSnackbar({
        color: 'error',
        text: 'Failed to update document',
      });

      isHidden.value = !newValue;
      throw e;
    }
  };

  const initFields = {
    atsEditUrl,
    atsName,
    atsIcon,
    companyName,
    hasIntegration,
    hasShareUsers,
    isLinked,
    isOpen,
    isPublished,
    isSharePage,
    isEditable,
    jobId,
    jobStatusLabel,
    ownersActive,
    ownersLead,
    projectIntegrationType,
    projectIntegrationTypeKey,
    shareNames,
    showInvite,
    superuser,
    tapwageId,
    templateList,
    validUsers,
    documentType,
    revision_id: revisionId,
    specificityKey,
    atsRequisitionId,
    isApplicationReportingEnabled,
    atsTotalApplicants,
    integrationTypeKey,
    isExternal,
    isEvergreen,
    isFlagship,
    showWeeklyReports,
  };
  const setInitFields = (initData) => {
    setFields(initData, initFields);
  };

  const documentFields = {
    allRevisions,
    bodyMessages,
    company,
    location,
    reqReportUrl,
    specificityData,
    score,
    scales,
    specificityKey,
    title,
    wordCount,
    isPrivate,
    flexible_id: flexibleId,
    isSyncable,
    isPermissionTogglable,
    isHidden,
  };

  const setDocumentFields = ({ documentData = {}, isRescore = false }) => {
    const newFields = documentData?.description_json?.fields || [];

    useDocumentEditor().updateDocumentData({
      fields: newFields,
      title: documentData.title,
      location: documentData.location,
      company: documentData.company,
      flexibleId: documentData.flexible_id,
    });
    useHighlights().updateBodyMessages({
      bodyMessages: JSON.parse(JSON.stringify(documentData.bodyMessages || {})),
      fields: newFields,
      title: documentData.title,
      location: documentData.location,
      isRescore,
    });
    if (!isRescore) {
      postSummary.value = documentData.postSummary;
    }

    setFields(documentData, documentFields);

    newFields.forEach((field, index) => {
      if (fields.value[index]) {
        fields.value[index].highlights = field.highlights;
      }
    });
  };

  const setIsPrivate = (newIsPrivate) => {
    isPrivate.value = newIsPrivate;
  };

  const setSaveRequestStatus = (newStatus) => {
    saveRequestStatus.value = newStatus;
  };

  const invokeSaveDebouncerNow = () => {
    if (saveDebounceHandler.value) {
      saveDebounceHandler.value.flush();
    }
    saveDebounceHandler.value = null;
  };

  const cancelSaveDebouncer = () => {
    if (saveDebounceHandler.value) {
      saveDebounceHandler.value.cancel();
    }
    saveDebounceHandler.value = null;
  };

  const setSaveDebouncer = (debouncer) => {
    saveDebounceHandler.value = debouncer;
  };

  const setShowDuplicateModal = (show) => {
    showDuplicateModal.value = show;
  };

  const makeBlockMoveAnimations = async (moveAnimations) => {
    if (!moveAnimations || moveAnimations.length === 0) {
      return;
    }

    const pageScroll = moveAnimations[0].diff;
    setMoveAnimation({ blockId: 'page', diff: pageScroll });
    moveAnimations.forEach((animation) => setMoveAnimation(animation));

    await new Promise((resolve) => setTimeout(resolve, 300));

    setMoveAnimation({ blockId: 'page', diff: 0 });
    moveAnimations.forEach(({ blockId }) => setMoveAnimation({ blockId, diff: 0 }));
  };

  const restoreFieldStructure = async ({
    fieldId, structure, focusBlock, moveAnimations,
  }) => {
    const fieldIndex = fields.value.findIndex((f) => f.id === fieldId);
    const newFieldStructure = structure[fieldId];

    if (fieldIndex < 0 || !newFieldStructure) {
      /* istanbul ignore next */
      return;
    }

    const newSections = newFieldStructure.sections.map((sectionStructure) => ({
      ...(sectionMap.value[sectionStructure.id] || sectionStructure),
      blocks: sectionStructure.blocks.map((blockId) => blockMap.value[blockId]).filter(Boolean),
    }));

    await makeBlockMoveAnimations(moveAnimations);
    updateFieldSections({ fieldIndex, sections: newSections });

    if (focusBlock) {
      await setSelectionPromise(focusBlock);
    }
  };

  const copyToClipboard = async ({ content, isHtml = false }) => {
    useAppBase().checkClipboardPermissions({});

    try {
      return writeToClipboardApi(content, isHtml);
    } catch (err) {
      return Promise.resolve(writeToClipboardExecCommand(content));
    }
  };

  const copyFieldToClipboard = (fieldId) => {
    const field = joinFooterAndHeaderFields(fields.value).find(({ id }) => fieldId === id);
    if (!field) {
      return;
    }

    copyToClipboard({ content: getFieldHtml(field), isHtml: true });
  };

  const copyAllFieldsToClipboard = (jobTitle) => {
    const htmlJobTitle = `<h2>${jobTitle}</h2>`;
    const html = fields.value.map(getFieldHtml);

    copyToClipboard({ content: [htmlJobTitle, ...html].join('\n'), isHtml: true });
  };

  const sectionExtraSpaceCorrection = ({
    isMoveUp, thisBlock, otherBlockId, blockMap: blockMapToUse,
  }) => {
    const extraSectionSpace = 12;

    // the one that got clicked on
    const thisBlockSection = thisBlock.sectionType;
    // the one that swaps position with thisBlock
    const otherBlock = blockMapToUse[otherBlockId] || {};
    const otherBlockSection = otherBlock.sectionType;

    // the block next to otherBlock, future neighbour of thisBlock
    const afterOtherBlockId = isMoveUp ? otherBlock.previousBlockId : otherBlock.nextBlockId;
    const afterOtherBlock = blockMapToUse[afterOtherBlockId] || {};
    const afterOtherBlockSection = afterOtherBlock.sectionType;

    // the block before thisBlock, future neighbour of otherBlock
    const beforeThisBlockId = isMoveUp ? thisBlock.nextBlockId : thisBlock.previousBlockId;
    const beforeThisBlock = blockMapToUse[beforeThisBlockId] || {};
    const beforeThisBlockSection = beforeThisBlock.sectionType;

    const thereIsExtraSpaceBeforeThis = thisBlockSection !== beforeThisBlockSection;
    const thereIsExtraSpaceBeforeOther = otherBlockSection !== afterOtherBlockSection;
    const thereWillBeExtraSpaceBeforeThis = thisBlockSection !== afterOtherBlockSection;
    const thereWillBeExtraSpaceBeforeOther = otherBlockSection !== beforeThisBlockSection;

    // same section - no correction
    if (thisBlock.sectionId === otherBlock.sectionId) {
      return { thisCorrection: 0, otherCorrection: 0 };
    }

    /**
     *  [s1 afterOtherBlock]          [s1 afterOtherBlock]
     *  [s1 otherBlock]               []
     *  []                            [s2 thisBlock]
     *  [s2 thisBlock]         ↑      []
     *  []                            [s1 otherBlock]
     *  [s3 beforeThisBlock]          []
     *                                [s3 beforeThisBlock]
     */
    if (isMoveUp && !thereIsExtraSpaceBeforeOther && thereWillBeExtraSpaceBeforeThis) {
      return { thisCorrection: 0, otherCorrection: extraSectionSpace * 2 };
    }

    /**
     *  [s1 afterOtherBlock]          [s1 afterOtherBlock]
     *  []                            [s1 thisBlock]
     *  [s2 otherBlock]               []
     *  []                            [s2 otherBlock]
     *  [s1 thisBlock]         ↑      []
     *  []                            [s3 beforeThisBlock]
     *  [s3 beforeThisBlock]
     */
    if (isMoveUp && thereIsExtraSpaceBeforeOther && !thereWillBeExtraSpaceBeforeThis) {
      return { thisCorrection: extraSectionSpace * 2, otherCorrection: 0 };
    }

    /**
     *  [s1 beforeThisBlock]          [s1 beforeThisBlock]
     *  [s1 thisBlock]         ↓      []
     *  []                            [s2 otherBlock]
     *  [s2 otherBlock]               []
     *  []                            [s1 thisBlock]
     *  [s3 afterOtherBlock]          []
     *                                [s3 afterOtherBlock]
     */
    if (!isMoveUp && !thereIsExtraSpaceBeforeThis && thereWillBeExtraSpaceBeforeOther) {
      return { thisCorrection: extraSectionSpace * 2, otherCorrection: 0 };
    }

    /**
     *  [s1 beforeThisBlock]          [s1 beforeThisBlock]
     *  []                            [s1 otherBlock]
     *  [s2 thisBlock]         ↓      []
     *  []                            [s2 thisBlock]
     *  [s1 otherBlock]               []
     *  []                            [s3 afterOtherBlock]
     *  [s3 afterOtherBlock]
     */
    if (!isMoveUp && thereIsExtraSpaceBeforeThis && !thereWillBeExtraSpaceBeforeOther) {
      return { thisCorrection: 0, otherCorrection: extraSectionSpace * 2 };
    }

    return { thisCorrection: extraSectionSpace, otherCorrection: extraSectionSpace };
  };

  const unmakeFlagship = async ({ jobId: unmakeJobId }) => {
    try {
      isFlagship.value = false;

      const { success } = await recruitJsonUnmakeFlagship({ jobId: unmakeJobId });

      if (success) {
        addSnackbar({
          color: 'success',
          text: 'Your team can now find this job in the Draft library',
          icon: '$checkbox-marked-circle',
        });
      } else {
        isFlagship.value = true;

        addSnackbar({
          color: 'error',
          text: 'Failed to revert job to a draft',
        });
      }
    } catch (error) {
      isFlagship.value = true;
      addSnackbar({
        color: 'error',
        text: 'Failed to revert job to a draft',
      });
      throw error;
    }
  };

  const duplicateAndMakeFlagship = async ({ jobId: duplicateJobId }) => {
    try {
      const { success, id } = await recruitJsonDuplicateFlagship(duplicateJobId);
      if (success && id) {
        addSnackbar({
          color: 'success',
          text: 'Your team can now find a copy of this job in the Flagship library',
          icon: '$checkbox-marked-circle',
        });
        return { id };
      }

      throw new Error('Failed to make flagship job');
    } catch (error) {
      addSnackbar({
        color: 'error',
        text: 'Failed to make flagship job',
      });
      throw error;
    }
  };

  const makeDraftPublic = async ({ jobId: privateJobId }) => {
    try {
      const { success } = await recruitJsonMakePublic({ jobId: privateJobId });
      if (success) {
        isPrivate.value = false;

        addSnackbar({
          color: 'success',
          text: 'This job is now available for the organization',
          icon: '$checkbox-marked-circle',
        });
      } else {
        addSnackbar({
          color: 'error',
          text: 'Failed to make job public',
        });
      }
    } catch (error) {
      addSnackbar({
        color: 'error',
        text: 'Failed to make job public',
      });
      throw error;
    }
  };

  const makeDraftPrivate = async ({ jobId: publicJobId }) => {
    try {
      const { success } = await recruitJsonMakePrivateUs({ jobId: publicJobId });
      if (success) {
        isPrivate.value = true;

        addSnackbar({
          color: 'success',
          text: 'This job is now hidden from the organization',
          icon: '$checkbox-marked-circle',
        });
      } else {
        addSnackbar({
          color: 'error',
          text: 'Failed to make job private',
        });
      }
    } catch (error) {
      addSnackbar({
        color: 'error',
        text: 'Failed to make job private',
      });
      throw error;
    }
  };

  // TODO: remove after new similar jobs implementation is complete
  const similarJobs = ref(null);
  const setSimilarJobs = (newSimilarJobs) => {
    similarJobs.value = newSimilarJobs;
  };

  return {
    fields,
    moveAnimation,
    loadingAnimation,
    saveRequestStatus,
    saveDebounceHandler,
    atsEditUrl,
    atsName,
    atsIcon,
    atsRequisitionId,
    atsTotalApplicants,
    companyName,
    documentType,
    flexibleId,
    hasIntegration,
    hasShareUsers,
    integrationTypeKey,
    isApplicationReportingEnabled,
    isPrivate,
    isExternal,
    isEvergreen,
    isLinked,
    isOpen,
    isHidden,
    isPublished,
    isSharePage,
    isEditable,
    jobStatusLabel,
    jobId,
    ownersActive,
    ownersLead,
    projectIntegrationType,
    projectIntegrationTypeKey,
    revisionId,
    shareNames,
    showInvite,
    showDuplicateModal,
    specificityKey,
    superuser,
    tapwageId,
    templateList,
    validUsers,
    wordCount,
    showWeeklyReports,
    bodyMessages,
    score,
    scales,
    specificityData,
    isSyncable,
    isPermissionTogglable,
    allRevisions,
    company,
    location,
    reqReportUrl,
    title,
    isFlagship,
    postSummary,

    // Getters
    document,
    isTemplate,
    documentTypeLabel,
    blockTypes,
    blockList,
    blockMap,
    sectionList,
    sectionMap,
    fieldStructure,
    revisionView,
    subMenuProps,
    isRevisionSaving,
    hasContent,
    sideBySideView,
    whichCompanyLocationLocked,
    isPublishedTemplate,
    isTapwageIntegration,
    showAtsMenu,
    showTapwageMenu,

    // Actions
    setSelectionPromise,
    getFieldHtml,
    findBlockById,
    setDescriptionJson,
    updateFieldSections,
    setBlockInitialized,
    setBlockHtml,
    setBlockType,
    setBlockLockType,
    setSectionType,
    setSectionlockType,
    swapBlocks,
    setDocumentType,
    setMoveAnimation,
    addToLoadingAnimation,
    setLoadingAnimation,
    setInitFields,
    setDocumentFields,
    setIsPrivate,
    setSaveRequestStatus,
    invokeSaveDebouncerNow,
    cancelSaveDebouncer,
    setSaveDebouncer,
    setShowDuplicateModal,
    makeBlockMoveAnimations,
    restoreFieldStructure,
    copyToClipboard,
    copyFieldToClipboard,
    copyAllFieldsToClipboard,
    sectionExtraSpaceCorrection,
    updateIsHidden,
    unmakeFlagship,
    duplicateAndMakeFlagship,
    makeDraftPublic,
    makeDraftPrivate,

    // TODO: remove after new similar jobs implementation is complete
    similarJobs,
    setSimilarJobs,
  };
});
